import {
  ArrowsRightLeftIcon,
  GlobeAltIcon,
  LanguageIcon,
  ShieldExclamationIcon,
  BoltIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

interface Feature {
  Icon: typeof BoltIcon
  title: string
  blurb: string
}

const FEATURES: Feature[] = [
  {
    Icon: ArrowsRightLeftIcon,
    title: 'Cross-source event matching',
    blurb:
      'Match whole fixture lists across bookmakers in one call — home/away pairs reconciled into a single event.',
  },
  {
    Icon: GlobeAltIcon,
    title: '8 sports supported',
    blurb:
      'Soccer, basketball, tennis, American football, volleyball, ice hockey, baseball and rugby — one API for all.',
  },
  {
    Icon: LanguageIcon,
    title: 'Diacritics & transliteration',
    blurb:
      'Mbappé = Mbappe, Greeklish spellings, and cross-alphabet variants all resolve to the same entity.',
  },
  {
    Icon: ShieldExclamationIcon,
    title: 'Confusable protection',
    blurb:
      'City rivals and same-surname players are kept apart — the model is trained on the lookalikes that trip up naïve matching.',
  },
  {
    Icon: BoltIcon,
    title: 'Built for speed',
    blurb:
      'Cached lookups return in sub-millisecond time, and batch endpoints resolve large fixture lists in seconds.',
  },
  {
    Icon: ArrowPathIcon,
    title: 'Feedback loop',
    blurb:
      'Report a false positive or false negative and it flows into a review queue — the models retrain on real mistakes.',
  },
]

export function Features() {
  return (
    <section className="bg-dash-surface">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-dash-text sm:text-4xl">
            Everything you need to reconcile sports data
          </h2>
          <p className="mt-4 text-lg text-dash-secondary">
            Purpose-built for the messy reality of naming across sportsbooks and feeds.
          </p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ Icon, title, blurb }) => (
            <div
              key={title}
              className="rounded-xl border border-dash-border bg-dash-bg p-6 transition-colors hover:border-dash-accent/50"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-dash-accent/10">
                <Icon className="h-6 w-6 text-dash-accent" aria-hidden="true" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-dash-text">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-dash-secondary">{blurb}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
